import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Plus, Trash2 } from "lucide-react";

import { addEmergencyAccount, listEmergencyAccounts, removeEmergencyAccount } from "@/api/tenants";
import { Field, Fieldset, Input } from "@/components/form";
import { ErrorBox, MutationError, Spinner, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import { Button, Notice } from "@/components/ui";

/**
 * The accounts that keep a password when the tenant signs in through the company directory only.
 * Unlike the directories, they are not part of a form: adding or removing one is applied at once,
 * and what the server refuses (the last account of a directory-only tenant, a sixth account) is
 * said as it rendered it.
 */
export function EmergencyAccounts({ tenantId }: { tenantId: string }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const token = auth.user?.id_token ?? "";
  const [email, setEmail] = useState("");

  const accounts = useQuery({
    queryKey: ["tenant", tenantId, "emergency-accounts"],
    queryFn: () => listEmergencyAccounts(token, tenantId),
    enabled: !!token && !!tenantId,
  });

  // The count shown on the tenant's page comes from its own metadata: refresh both.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
  };

  const addMut = useMutation({
    mutationFn: (value: string) => addEmergencyAccount(token, tenantId, value),
    onSuccess: () => {
      setEmail("");
      refresh();
    },
  });
  const removeMut = useMutation({
    mutationFn: (username: string) => removeEmergencyAccount(token, tenantId, username),
    onSuccess: refresh,
  });

  function onAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    addMut.mutate(email.trim());
  }

  const failure = loadFailure(accounts);
  const items = accounts.data?.items ?? [];

  return (
    <Fieldset
      legend="Emergency accounts"
      help="Accounts that keep a password when sign-in goes through the company directory only. Each new account is an admin of every environment and receives an invitation email."
    >
      <div className="mt-3 space-y-4">
        {isLoadingOrPaused(accounts) && <Spinner />}
        {failure && (
          <ErrorBox
            error={failure.error}
            title="Could not load the emergency accounts"
            onRetry={failure.onRetry}
            retrying={failure.retrying}
          />
        )}
        {accounts.data && items.length === 0 && (
          <p className="text-sm text-ink-muted">No emergency account yet.</p>
        )}
        {items.length > 0 && (
          <ul className="space-y-2">
            {items.map((account) => (
              <li key={account.username} className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="min-w-0 break-all text-ink">{account.email}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-xs text-ink-muted">{account.status}</span>
                    <Button
                      variant="ghost"
                      icon={Trash2}
                      loading={removeMut.isPending && removeMut.variables === account.username}
                      disabled={removeMut.isPending}
                      onClick={() => removeMut.mutate(account.username)}
                    >
                      Remove
                    </Button>
                  </span>
                </div>
                {account.status === "missing" && (
                  <Notice tone="warning">This account no longer exists in the sign-in pool.</Notice>
                )}
              </li>
            ))}
          </ul>
        )}
        <MutationError mutation={removeMut} title="Could not remove the emergency account" />

        <form onSubmit={onAdd} className="flex items-end gap-3">
          <Field label="Email" className="flex-1">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={254} />
          </Field>
          <Button type="submit" variant="secondary" icon={Plus} loading={addMut.isPending}>
            Add emergency account
          </Button>
        </form>
        <MutationError mutation={addMut} title="Could not add the emergency account" />
      </div>
    </Fieldset>
  );
}
